import { INVOICE_LOCAL_PAGE_TYPE, InvoiceLocalProps } from "./types";
import { CSSProperties } from "styled-components";
import { messages } from "./messages";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import { useFormatter } from "hooks/utils/useFormatter";
import { useCurrency } from "hooks/user/useCurrencies";
import {
  CreditNote,
  INVOICE_WITHHOLDING_TYPE,
  InvoiceWithholding,
} from "@letsbit/malcolmlb";
import CustomFormattedMessage from "components/CustomFormattedMessage";
import { InvoiceSeparator } from "../InvoiceSeparator";
import { InvoicePageA4 } from "../InvoicePageA4";
import { ImgUrlToBase64 } from "../ImgUrlToBase64";
import { useEffect, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

const HeaderLabel: React.FC<{
  label: string;
  value: string;
  styles?: CSSProperties;
}> = (params) => {
  return (
    <div style={{ display: "flex", flexDirection: "row", ...params.styles }}>
      <h3 style={{ fontSize: 18, marginRight: 4, fontWeight: 700 }}>
        {params.label}
      </h3>
      <p style={{ fontSize: 18 }}>{params.value}</p>
    </div>
  );
};

const InvoicePage: React.FC<
  | { type: INVOICE_LOCAL_PAGE_TYPE.INVOICE; invoice: InvoiceWithholding }
  | { type: INVOICE_LOCAL_PAGE_TYPE.CREDIT_NOTE; invoice: CreditNote }
> = ({ type, invoice }) => {
  const { formatValue } = useFormatter();
  const { formatMessage } = useIntl();
  const currency = useCurrency(invoice.currency);
  const qrRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const replaceQrPng = async (ref: HTMLCanvasElement) => {
      const imgUrl = ref.toDataURL("image/png");
      const img = document.createElement("img");
      (img.style as any).width = "150px";
      (img.style as any).height = "150px";
      img.src = imgUrl;
      if (ref.parentElement) {
        const parent = ref.parentElement;
        parent.removeChild(ref);
        parent.prepend(img);
      }
    };

    if (qrRef.current) {
      replaceQrPng(qrRef.current);
    }
  }, []);

  const getCode = () => {
    if (type === INVOICE_LOCAL_PAGE_TYPE.INVOICE) {
      switch (invoice.type) {
        case INVOICE_WITHHOLDING_TYPE.AFIP_A:
          return "01";
        case INVOICE_WITHHOLDING_TYPE.AFIP_B:
          return "06";
        case INVOICE_WITHHOLDING_TYPE.AFIP_C:
          return "11";
        default:
          return "invalid";
      }
    } else {
      switch (invoice.type) {
        case INVOICE_WITHHOLDING_TYPE.AFIP_A:
          return "03";
        case INVOICE_WITHHOLDING_TYPE.AFIP_B:
          return "08";
        case INVOICE_WITHHOLDING_TYPE.AFIP_C:
          return "13";
        default:
          return "invalid";
      }
    }
  };

  return (
    <InvoicePageA4>
      <InvoiceSeparator />
      {/* HEADER */}
      <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "50%",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 20,
          }}
        >
          <ImgUrlToBase64
            src={invoice.sales_point.image_url}
            width={250}
            height={80}
          />
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>
            {invoice.sales_point.legal_name}
          </h1>
          <h3 style={{ fontSize: 16 }}>{invoice.sales_point.address}</h3>
          <h3 style={{ fontSize: 16 }}>
            Tel. {invoice.sales_point.phone_number}
          </h3>
          <h1 style={{ fontSize: 18, fontWeight: 700 }}>
            IVA{" "}
            {formatMessage(
              messages[invoice.sales_point.tax_condition] ?? messages.default,
            ).toUpperCase()}
          </h1>
        </div>
        <div style={{ display: "flex", width: "50%", marginBottom: 20 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: 100,
              height: 100,
              backgroundColor: "#eaeaea",
              marginRight: 50,
              marginTop: 4,
            }}
          >
            <p style={{ fontSize: 70, fontWeight: 700, marginTop: -20 }}>
              {formatMessage(messages[invoice.type] ?? messages.default)}
            </p>
            <p style={{ fontSize: 16, fontWeight: 700 }}>COD. {getCode()}</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h1 style={{ fontSize: 20, marginBottom: 8, fontWeight: 700 }}>
              {formatMessage(messages[type] ?? messages.default)}{" "}
              {formatMessage(messages[invoice.type] ?? messages.default)}
            </h1>
            <HeaderLabel
              label="Comp. Nro: "
              value={
                invoice.sales_point.external_id +
                " - " +
                invoice.external_number
              }
            />
            <HeaderLabel
              label="Sucursal: "
              value={invoice.sales_point.branch}
            />
            <HeaderLabel
              label={"Fecha Emisión:"}
              value={dayjs(invoice.date).format("DD/MM/YYYY")}
              styles={{ marginBottom: 8 }}
            />
            <HeaderLabel
              label="CUIT: "
              value={invoice.sales_point.national_id}
            />
            <HeaderLabel
              label="Ing. Brutos: "
              value={invoice.sales_point.gross_income_id}
            />
            {/* TO REVIEW */}
            <HeaderLabel label="Inicio de Actividad: " value="01/04/2018" />
          </div>
        </div>
      </div>
      <InvoiceSeparator />
      {/* CLIENT DATA */}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          marginTop: 8,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "50%",
          }}
        >
          <HeaderLabel label="Nombre: " value={invoice.member.legal_name} />
          <HeaderLabel
            label="Documento: "
            value={invoice.member.national_id}
            styles={{ marginBottom: 8 }}
          />
          <HeaderLabel label="Concepto: " value="Servicios" />
          <HeaderLabel
            label="Condición de Venta: "
            value="Cta. Cte."
            styles={{ marginBottom: 8 }}
          />
        </div>
        <div style={{ display: "flex", width: "50%", flexDirection: "column" }}>
          <HeaderLabel label="Domicilio: " value="Santa Fe" />
          <HeaderLabel
            label="Condición frente al IVA: "
            value={formatMessage(
              messages[invoice.member.tax_condition] ?? messages.default,
            )}
            styles={{ marginBottom: 8 }}
          />
          <HeaderLabel
            label="Período de Servicio: "
            value={`${dayjs(invoice.date).format("DD/MM/YYYY")} al ${dayjs(invoice.date).format("DD/MM/YYYY")}`}
          />
          <HeaderLabel
            label="Fecha Vto. Pago: "
            value={dayjs(invoice.due_date).format("DD/MM/YYYY")}
            styles={{ marginBottom: 8 }}
          />
        </div>
      </div>

      <InvoiceSeparator />
      {/* TABLE */}
      <div style={{ width: "100%", height: 400 }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          {/* TABLE HEADER */}
          <tr
            style={{
              backgroundColor: "#eaeaea",
            }}
          >
            <td
              style={{
                padding: "10px",
              }}
            >
              Cod.
            </td>
            <td
              style={{
                padding: "10px",
                // maxWidth: 250,
              }}
            >
              Concepto
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              Cant.
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              Unidad
            </td>
            <td
              style={{
                padding: "10px",
                width: 150,
              }}
            >
              Precio Unit. (
              {currency?.ticker_id.toUpperCase() ||
                invoice.currency.toUpperCase()}
              )
            </td>
            <td
              style={{
                padding: "10px",
                width: 90,
              }}
            >
              % Bonif.
            </td>
            <td
              style={{
                padding: "10px",
                width: 120,
              }}
            >
              Subtotal (USD)
            </td>
            <td
              style={{
                padding: "10px",
                width: 100,
              }}
            >
              Alic. IVA
            </td>
            <td
              style={{
                padding: "10px",
                width: 120,
              }}
            >
              Importe (
              {currency?.ticker_id.toUpperCase() ||
                invoice.currency.toUpperCase()}
              )
            </td>
          </tr>
          {/* TABLE CONTENT */}
          <tr style={{ textAlign: "center" }}>
            <td
              style={{
                padding: "10px",
              }}
            >
              1
            </td>
            <td
              style={{
                padding: "10px",
                // maxWidth: 250,
                textAlign: "left",
              }}
            >
              {type === INVOICE_LOCAL_PAGE_TYPE.INVOICE
                ? invoice.description
                : ""}
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              1
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              unidad
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              {formatValue(
                (+invoice.net_amount).toString(),
                currency?.precision,
              )}
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              0.00
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              {formatValue(
                (+invoice.net_amount).toString(),
                currency?.precision,
              )}
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              {formatValue(
                (+(
                  type === INVOICE_LOCAL_PAGE_TYPE.INVOICE
                    ? invoice.invoice_taxes[0]
                    : invoice.credit_note_taxes[0]
                ).amount).toString(),
                currency?.precision,
              )}
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              {formatValue(
                (+invoice.net_amount + +invoice.tax_amount).toString(),
                currency?.precision,
              )}
            </td>
          </tr>
        </table>
      </div>
      <InvoiceSeparator />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "end",
          justifyContent: "end",
        }}
      >
        <h3
          style={{ fontSize: 18, textDecoration: "underline", fontWeight: 700 }}
        >
          Moneda:{" "}
          {currency?.ticker_id.toUpperCase() || invoice.currency.toUpperCase()}{" "}
          - {currency?.name}
        </h3>
        <table style={{ width: "40%", justifyContent: "right" }}>
          {/* TABLE HEADER */}
          <tr>
            <td
              style={{
                padding: "10px",
                fontWeight: 700,
              }}
            >
              Importe Neto Gravado:
            </td>
            <td
              style={{
                padding: "10px",
                fontWeight: 700,
              }}
            >
              {currency?.ticker_id.toUpperCase() ||
                invoice.currency.toUpperCase()}{" "}
              {formatValue(
                (+invoice.net_amount).toString(),
                currency?.precision,
              )}
            </td>
          </tr>
          {type === INVOICE_LOCAL_PAGE_TYPE.INVOICE
            ? invoice.invoice_taxes.map((tax) => (
                <tr>
                  <td
                    style={{
                      padding: "10px",
                      fontWeight: 700,
                    }}
                  >
                    Importe {tax.general_tax.description}:
                  </td>
                  <td
                    style={{
                      padding: "10px",
                    }}
                  >
                    {currency?.ticker_id.toUpperCase() ||
                      invoice.currency.toUpperCase()}{" "}
                    {formatValue((+tax.amount).toString(), currency?.precision)}
                  </td>
                </tr>
              ))
            : invoice.credit_note_taxes.map((tax) => (
                <tr>
                  <td
                    style={{
                      padding: "10px",
                      fontWeight: 700,
                    }}
                  >
                    Importe {tax.general_tax.description}:
                  </td>
                  <td
                    style={{
                      padding: "10px",
                    }}
                  >
                    {currency?.ticker_id.toUpperCase() ||
                      invoice.currency.toUpperCase()}{" "}
                    {formatValue((+tax.amount).toString(), currency?.precision)}
                  </td>
                </tr>
              ))}

          <tr>
            <td
              style={{
                padding: "10px",
                fontWeight: 700,
              }}
            >
              Importe Neto No Gravado:
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              {currency?.ticker_id.toUpperCase() ||
                invoice.currency.toUpperCase()}{" "}
              0.00
            </td>
          </tr>
          <tr>
            <td
              style={{
                padding: "10px",
                fontWeight: 700,
              }}
            >
              Importe Exento:
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              {currency?.ticker_id.toUpperCase() ||
                invoice.currency.toUpperCase()}{" "}
              0.00
            </td>
          </tr>
          <tr style={{ backgroundColor: "#fafafa" }}>
            <td
              style={{
                padding: "10px",
                fontWeight: 700,
              }}
            >
              Importe Total:
            </td>
            <td
              style={{
                padding: "10px",
              }}
            >
              {currency?.ticker_id.toUpperCase() ||
                invoice.currency.toUpperCase()}{" "}
              {formatValue(
                (+invoice.net_amount + +invoice.tax_amount).toString(),
                currency?.precision,
              )}
            </td>
          </tr>
          {invoice.currency_price && (
            <tr style={{ backgroundColor: "#fafafa" }}>
              <td
                style={{
                  padding: "10px",
                }}
              >
                Importe Total en Pesos Argentinos (Cotizacion a{" "}
                {invoice.currency_price}):
              </td>
              <td
                style={{
                  padding: "10px",
                }}
              >
                ${" "}
                {formatValue(
                  (
                    (+invoice.net_amount + +invoice.tax_amount) *
                    +invoice.currency_price
                  ).toString(),
                  2,
                )}
              </td>
            </tr>
          )}
        </table>
      </div>

      <InvoiceSeparator />

      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >
        <QRCodeCanvas ref={qrRef} value={invoice.qr} size={150} />
        <p style={{ fontSize: 14, width: "45%" }}>
          <CustomFormattedMessage {...messages.legalText} />
        </p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "end",
          }}
        >
          <HeaderLabel label="CAE Nro.: " value={invoice.external_id} />
          <HeaderLabel
            label="Fecha Vto. CAE: "
            value={dayjs(invoice.external_due_date).format("DD/MM/YYYY")}
          />
        </div>
      </div>
    </InvoicePageA4>
  );
};

export const InvoiceLocal: React.FC<InvoiceLocalProps> = ({ invoice }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <InvoicePage type={INVOICE_LOCAL_PAGE_TYPE.INVOICE} invoice={invoice} />
      {invoice.credit_notes.map((creditNote) => (
        <InvoicePage
          type={INVOICE_LOCAL_PAGE_TYPE.CREDIT_NOTE}
          invoice={creditNote}
        />
      ))}
    </div>
  );
};
